import {useEffect} from 'react';

type SeoProps = {
    title: string;
    description: string;
    canonicalPath?: string;
    type?: 'website' | 'article';
    structuredData?: Record<string, unknown> | Record<string, unknown>[];
};

const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://promotifyai.com';
const SITE_NAME = 'Promotify';

const upsertMeta = (selector: string, attributes: Record<string, string>) => {
    let element = document.head.querySelector<HTMLMetaElement>(selector);

    if (!element) {
        element = document.createElement('meta');
        document.head.appendChild(element);
    }

    const metaElement = element;

    Object.entries(attributes).forEach(([key, value]) => {
        metaElement.setAttribute(key, value);
    });
};

const upsertLink = (rel: string, href: string) => {
    let element = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);

    if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
    }

    element.setAttribute('href', href);
};

const Seo = ({title, description, canonicalPath = '/', type = 'website', structuredData}: SeoProps) => {
    useEffect(() => {
        const canonicalUrl = new URL(canonicalPath, SITE_URL).toString();
        const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
        const jsonLd = structuredData ? JSON.stringify(structuredData) : '';

        document.title = fullTitle;

        upsertMeta('meta[name="description"]', {name: 'description', content: description});
        upsertMeta('meta[name="robots"]', {name: 'robots', content: 'index,follow,max-image-preview:large'});
        upsertMeta('meta[property="og:site_name"]', {property: 'og:site_name', content: SITE_NAME});
        upsertMeta('meta[property="og:title"]', {property: 'og:title', content: fullTitle});
        upsertMeta('meta[property="og:description"]', {property: 'og:description', content: description});
        upsertMeta('meta[property="og:type"]', {property: 'og:type', content: type});
        upsertMeta('meta[property="og:url"]', {property: 'og:url', content: canonicalUrl});
        upsertMeta('meta[name="twitter:card"]', {name: 'twitter:card', content: 'summary_large_image'});
        upsertMeta('meta[name="twitter:title"]', {name: 'twitter:title', content: fullTitle});
        upsertMeta('meta[name="twitter:description"]', {name: 'twitter:description', content: description});
        upsertLink('canonical', canonicalUrl);

        let script = document.head.querySelector<HTMLScriptElement>('script[data-seo-jsonld="true"]');

        if (jsonLd) {
            if (!script) {
                script = document.createElement('script');
                script.type = 'application/ld+json';
                script.dataset.seoJsonld = 'true';
                document.head.appendChild(script);
            }

            script.text = jsonLd;
        } else if (script) {
            script.remove();
        }
    }, [title, description, canonicalPath, type, structuredData]);

    return null;
};

export default Seo;
